import Phaser from 'phaser';
import { HAPTICS, MAX_SIM_STEPS_PER_FRAME, PALETTE, SIM_STEP, SPRINT } from '../../config';
import { createAthleteView, type AthleteView } from '../../game/AthleteView';
import { AudioEngine } from '../../game/AudioEngine';
import { getDiscipline } from '../../game/data/disciplines';
import { getSkin } from '../../game/data/skins';
import { countryFlag } from '../../game/data/flags';
import { pickOpponents } from '../../game/data/aiRoster';
import { t } from '../../game/i18n';
import type { DisciplineResult, Standing } from '../../game/types';
import { Button } from '../../ui/Button';
import { PowerMeter } from '../../ui/PowerMeter';
import { TapZones } from '../../ui/TapZones';
import { viewport } from '../../ui/viewport';
import { DisciplineScene } from './DisciplineScene';

type Side = 'L' | 'R';
type Phase = 'intro' | 'showcase' | 'runup' | 'flight' | 'between' | 'done';
const ATTEMPTS = 3;

const START_X = 160;
const RUNWAY_M = 26;
const PPM = SPRINT.pixelsPerMeter;
const ANGLE_MIN = 18;
const ANGLE_MAX = 68;
const ANGLE_TARGET = 0.48; // sweet spot on the 0..1 sweep (~42°)
const RUN_FRACTION = 0.6; // first 60% of the runway = mash zone; rest = aim & throw zone

/**
 * Javelin — two-stage: in the first ~60% of the runway you MASH SX/DX to build speed; once
 * you cross into the throw zone the speed is LOCKED and you glide slowly to the line, free to
 * watch the sweeping ANGLE meter and tap LANCIA at the right moment. Overstep = foul.
 */
export class Javelin extends DisciplineScene {
  private phase: Phase = 'intro';
  private rig!: AthleteView;

  private velocity = 0;
  private runDist = 0;
  private acc = 0;
  private lastSide: Side | null = null;
  private cadence = 0;
  private lastTapAt = 0;
  private maxSpeed: number = SPRINT.maxSpeed;
  private impulseScale = 1;
  private reach = 45;

  private aiming = false; // crossed into the throw zone: speed locked, now aim + release
  private acquiredSpeed = 0;
  private runEndX = 0;
  private angleSweep = 0;
  private throwDist = 0;
  private foul = false;
  private flightT = 0;
  private flightDur = 1;
  private releaseX = 0;
  private releaseAngleRad = 0;
  private javelin!: Phaser.GameObjects.Graphics;
  private attempt = 1;
  private playerBest = 0;
  private aiIndex = 0;
  private playerMarker?: Phaser.GameObjects.GameObject[];

  private W = 0;
  private H = 0;
  private groundY = 0;
  private lineX = 0;
  private uiS = 1;
  private camTarget = 0;
  private aiThrows: { name: string; species: string; nationality: string; metric: number; skin: string }[] = [];
  private aiRig?: AthleteView;

  private speedBar!: PowerMeter;
  private angleBar!: PowerMeter;
  private hud!: Phaser.GameObjects.Text;
  private bigText!: Phaser.GameObjects.Text;
  private throwBtn!: Button;
  private tapZones!: TapZones;

  constructor() {
    super('Javelin');
  }

  create(): void {
    super.create();
    this.phase = 'intro';
    this.velocity = 0; this.runDist = 0; this.acc = 0; this.cadence = 0; this.lastSide = null;
    this.angleSweep = 0; this.throwDist = 0; this.foul = false; this.flightT = 0; this.camTarget = 0;
    this.aiming = false; this.acquiredSpeed = 0; this.attempt = 1; this.playerBest = 0; this.aiIndex = 0; this.playerMarker = undefined;

    const { w, h, s } = viewport(this);
    this.W = w; this.H = h; this.uiS = s;
    this.groundY = h - Math.max(96, h * 0.2);
    this.lineX = START_X + RUNWAY_M * PPM;
    this.runEndX = START_X + RUNWAY_M * RUN_FRACTION * PPM;
    const speedFactor = 0.7 + (this.athlete.stats.speed / 100) * 0.6;
    this.maxSpeed = SPRINT.maxSpeed * speedFactor;
    this.impulseScale = speedFactor;
    this.reach = 32 + (this.athlete.stats.power / 100) * 30;

    const src = this.textures.get('stadium').getSourceImage();
    this.add.image(0, 0, 'stadium').setOrigin(0, 0).setScrollFactor(0).setDepth(-10)
      .setScale(Math.max(w / src.width, (this.groundY + 30) / src.height));
    this.drawField();
    this.rig = createAthleteView(this, START_X, this.groundY, this.athlete, Phaser.Math.Clamp(h * 0.36, 180, 340));
    this.rig.container.setDepth(6);
    this.javelin = this.add.graphics().setDepth(8);
    this.fetchWorldRecord('javelin', 'distance', (m) => this.drawGroundRecord(this.lineX + m * PPM, this.groundY, this.groundY - Math.max(80, this.H * 0.22), `${m.toFixed(2)}m`));

    const opponents = pickOpponents(getSkin(this.athlete.skin).id, 3);
    this.aiThrows = opponents.map((o) => ({ name: o.name, species: o.species, nationality: o.nationality, skin: o.skin, metric: Math.max(20, 30 + (90 - o.sprintTime * 4) * 0.4 + (Math.random() * 12 - 6)) }));

    this.buildHud();
    this.controls.on('tapLeft', () => this.onTap('L'));
    this.controls.on('tapRight', () => this.onTap('R'));
    this.controls.on('action', () => this.release());
    this.startCountdown();
  }

  private drawField(): void {
    const endX = this.lineX + 80 * PPM;
    const g = this.add.graphics().setDepth(-6);
    g.fillStyle(PALETTE.track, 1).fillRect(0, this.groundY, this.lineX + 6, this.H - this.groundY);
    // Throw zone tint (between the run-end line and the foul line).
    g.fillStyle(PALETTE.laneAccent, 0.14).fillRect(this.runEndX, this.groundY, this.lineX - this.runEndX, this.H - this.groundY);
    g.fillStyle(PALETTE.bad, 1).fillRect(this.lineX - 3, this.groundY, 6, 24); // foul line
    g.fillStyle(0x4e8b4e, 1).fillRect(this.lineX + 6, this.groundY, endX, this.H - this.groundY); // grass field
    // Dashed boundary at the run-end (mash zone -> aim zone) and at the foul line.
    const dashes = (x: number, color: number): void => {
      g.fillStyle(color, 0.85);
      const top = this.groundY - Math.max(60, this.H * 0.16);
      for (let yy = top; yy < this.groundY; yy += 16) g.fillRect(x - 2, yy, 4, 9);
    };
    dashes(this.runEndX, PALETTE.cadence);
    dashes(this.lineX, PALETTE.bad);
    const lblY = this.groundY - Math.max(64, this.H * 0.17);
    this.add.text((START_X + this.runEndX) / 2, lblY, t('game.runZone'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: '15px', color: '#bfe6ff', fontStyle: 'bold' }).setOrigin(0.5, 1).setDepth(-6);
    this.add.text((this.runEndX + this.lineX) / 2, lblY, t('game.aimZone'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: '15px', color: '#ffd9a8', fontStyle: 'bold' }).setOrigin(0.5, 1).setDepth(-6);
    for (let m = 10; m <= 80; m += 10) {
      const x = this.lineX + m * PPM;
      g.fillStyle(0x66a366, 1).fillRect(x, this.groundY, 2, 16);
      this.add.text(x, this.groundY + 20, `${m}m`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: '14px', color: '#cfe6cf' }).setOrigin(0.5, 0).setDepth(-6);
    }
    this.cameras.main.setBounds(0, 0, endX + this.W, this.H);
  }

  private buildHud(): void {
    const fs = (n: number) => `${Math.max(13, Math.round(n * this.uiS))}px`;
    this.hud = this.add.text(this.W / 2, 16, t('game.hudRunup'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(28), color: '#f4f7ff', fontStyle: 'bold' })
      .setOrigin(0.5, 0).setScrollFactor(0).setShadow(0, 3, '#000000aa', 5);
    // Bars live at the TOP (under the HUD) so the bottom + centre stay clear for the athlete
    // and the throw arc; the angle bar sits in the reading zone where you must "stop" it.
    const barW = Math.min(280, this.W * 0.42);
    const topY = Math.max(84, this.H * 0.15); // below the top-right quit button
    this.speedBar = new PowerMeter(this, 30 + barW / 2, topY, { width: barW, height: 16 });
    this.add.text(30 + barW / 2, topY - 18, t('hud.speed'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(12), color: '#d3dbf2' }).setOrigin(0.5).setScrollFactor(0);
    this.angleBar = new PowerMeter(this, this.W - 30 - barW / 2, topY, { width: barW, height: 16, target: [ANGLE_TARGET - 0.1, ANGLE_TARGET + 0.1], fillColor: PALETTE.cadence });
    this.add.text(this.W - 30 - barW / 2, topY - 18, t('hud.angle'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(12), color: '#d3dbf2' }).setOrigin(0.5).setScrollFactor(0);
    this.bigText = this.add.text(this.W / 2, this.H * 0.4, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(84), color: '#ffd24a', fontStyle: 'bold' })
      .setOrigin(0.5).setScrollFactor(0).setShadow(0, 8, '#00000088', 12);
    // Action button: compact, bottom-centre, semi-transparent — no longer covers the athlete.
    const label = this.sys.game.device.os.desktop ? `🎯 ${t('btn.throw')} ${t('btn.space')}` : `🎯 ${t('btn.throw')}`;
    this.throwBtn = new Button(this, this.W / 2, this.H - Math.max(30, 38 * this.uiS), label, () => this.release(), {
      width: Math.min(200, this.W * 0.4), height: Math.max(52, 58 * this.uiS), fontSize: Math.max(18, Math.round(22 * this.uiS)), color: PALETTE.power,
    });
    this.throwBtn.setScrollFactor(0).setAlpha(0.92).setVisible(false);
    this.tapZones = new TapZones(this);
    this.tapZones.setVisible(false);
  }

  private startCountdown(): void {
    ['3', '2', '1', t('game.go')].forEach((label, i) => {
      this.time.delayedCall(i * 650, () => {
        this.bigText.setText(label).setAlpha(1).setScale(1.5);
        this.tweens.add({ targets: this.bigText, scale: 1, duration: 300, ease: 'Back.out' });
        if (label === t('game.go')) {
          AudioEngine.go();
          this.tweens.add({ targets: this.bigText, alpha: 0, delay: 250, duration: 200 });
          this.runAiShowcase(); // rivals throw first, then the player's 3 attempts
        } else { AudioEngine.countdownTick(); }
      });
    });
  }

  /** Each rival's animal runs up and throws to their best distance, then plants a flag. */
  private runAiShowcase(): void {
    this.phase = 'showcase';
    if (this.aiIndex >= this.aiThrows.length) {
      this.rig.container.setVisible(true);
      this.beginAttempt();
      return;
    }
    this.rig.container.setVisible(false); // hide the player while the rivals throw
    const a = this.aiThrows[this.aiIndex]!;
    this.hud.setText(`🎯 ${a.name}`);
    this.aiRig?.destroy();
    this.aiRig = createAthleteView(this, START_X, this.groundY, { skin: a.skin }, Phaser.Math.Clamp(this.H * 0.36, 180, 340));
    this.aiRig.container.setDepth(6);
    const run = { x: START_X };
    this.tweens.add({
      targets: run, x: this.lineX, duration: 750, ease: 'Sine.in',
      onUpdate: () => {
        this.aiRig!.container.x = run.x;
        this.aiRig!.setMotion(0.9);
        this.aiRig!.update(1 / 60);
        this.camTarget = Phaser.Math.Clamp(run.x - this.W * 0.35, 0, this.cameras.main.getBounds().width - this.W);
      },
      onComplete: () => { this.aiRig!.setMotion(0); this.aiRig!.setPose('celebrate'); this.aiThrowArc(a); },
    });
  }

  private aiThrowArc(a: { name: string; nationality: string; metric: number }): void {
    const landX = this.lineX + a.metric * PPM;
    this.camTarget = Phaser.Math.Clamp(landX - this.W * 0.5, 0, this.cameras.main.getBounds().width - this.W);
    const obj = { p: 0 };
    this.tweens.add({
      targets: obj, p: 1, duration: 650, ease: 'Sine.in',
      onUpdate: () => {
        const x = Phaser.Math.Linear(this.lineX, landX, obj.p);
        const y = this.groundY - 60 - Math.sin(obj.p * Math.PI) * this.H * 0.3;
        this.drawSpear(x, y, -0.6 * Math.cos(obj.p * Math.PI), 70, true);
      },
      onComplete: () => {
        this.javelin.clear();
        this.flagMarker(landX, PALETTE.cadence, `${countryFlag(a.nationality)} ${a.name.split(' ')[0]} · ${a.metric.toFixed(2)}m`, false);
        AudioEngine.thud();
        this.tweens.add({
          targets: this.aiRig!.container, alpha: 0, duration: 250,
          onComplete: () => { this.aiRig?.destroy(); this.aiRig = undefined; this.aiIndex++; this.runAiShowcase(); },
        });
      },
    });
  }

  /** Start one of the player's three attempts from the top of the runway. */
  private beginAttempt(): void {
    this.velocity = 0; this.runDist = 0; this.cadence = 0; this.lastSide = null;
    this.aiming = false; this.acquiredSpeed = 0; this.foul = false; this.flightT = 0;
    this.phase = 'runup';
    this.javelin.clear();
    this.bigText.setAlpha(0);
    this.rig.container.setAngle(0).setAlpha(1);
    this.rig.container.x = START_X; this.rig.container.y = this.groundY;
    this.rig.setPose('idle');
    this.controls.setEnabled(true);
    this.lastTapAt = this.time.now / 1000;
    this.throwBtn.setVisible(true);
    this.speedBar.setVisible(true);
    this.tapZones.setVisible(true);
    this.camTarget = 0;
    this.cameras.main.scrollX = 0;
    this.hud.setText(t('game.attemptRun', { n: this.attempt, max: ATTEMPTS }));
  }

  /** A flag marker on the field (rival in cyan, player in gold). */
  private flagMarker(x: number, color: number, label: string, mine: boolean): Phaser.GameObjects.GameObject[] {
    const poleH = Math.max(46, this.H * 0.11);
    const top = this.groundY - poleH;
    const g = this.add.graphics().setDepth(mine ? 6 : 4);
    g.fillStyle(0xffffff, 0.85).fillRect(x - 1.5, top, 3, poleH);
    g.fillStyle(color, 1).fillTriangle(x + 1.5, top, x + 1.5, top + 18, x + 26, top + 9);
    g.fillStyle(color, 1).fillCircle(x, this.groundY, 4);
    const t = this.add.text(x, top - 6, label, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(12 * this.uiS)}px`, color: '#0b1020', fontStyle: 'bold',
      backgroundColor: mine ? '#ffd24a' : '#bfe6ff', padding: { x: 5, y: 2 },
    }).setOrigin(0.5, 1).setDepth(mine ? 7 : 5);
    return [g, t];
  }

  private onTap(side: Side): void {
    if (this.phase !== 'runup' || this.aiming) return; // no more mashing once in the throw zone
    const now = this.time.now / 1000;
    const dt = Math.max(0.001, now - this.lastTapAt);
    this.cadence = Phaser.Math.Linear(this.cadence, 1 / dt, 0.45);
    this.lastTapAt = now;
    const alternated = side !== this.lastSide;
    this.lastSide = side;
    let mult = alternated ? 1 : SPRINT.sameSidePenalty;
    if (this.cadence >= SPRINT.idealCadenceMin && this.cadence <= SPRINT.idealCadenceMax && alternated) mult *= SPRINT.flowBonus;
    this.velocity = Math.min(this.maxSpeed * 1.06, this.velocity + SPRINT.tapImpulse * this.impulseScale * mult);
    this.tapZones.flash(side);
    AudioEngine.tap(alternated ? 0.6 : 0.2);
    this.vibrate(HAPTICS.tap);
  }

  private release(): void {
    if (this.phase !== 'runup' || !this.rig) return;
    const athleteX = START_X + this.runDist * PPM;
    this.foul = athleteX > this.lineX + 6;
    // Distance uses the speed locked when entering the throw zone (or current if released early).
    const spd = this.acquiredSpeed > 0 ? this.acquiredSpeed : this.velocity;
    const speedNorm = Phaser.Math.Clamp(spd / this.maxSpeed, 0, 1);
    const angleQ = Phaser.Math.Clamp(1 - Math.abs(this.angleSweep - ANGLE_TARGET) / 0.5, 0.15, 1);
    this.throwDist = this.foul ? 0 : Math.max(0, (15 + speedNorm * this.reach) * angleQ);
    this.releaseX = Math.min(athleteX, this.lineX);
    const angleDeg = ANGLE_MIN + this.angleSweep * (ANGLE_MAX - ANGLE_MIN);
    this.releaseAngleRad = Phaser.Math.DegToRad(angleDeg);
    this.phase = 'flight';
    this.flightT = 0;
    this.flightDur = this.foul ? 0.5 : Phaser.Math.Clamp(this.throwDist / 30, 0.8, 2.0);
    this.controls.setEnabled(false);
    this.throwBtn.setVisible(false);
    this.tapZones.setVisible(false);
    this.rig.setMotion(0);
    this.rig.setPose(this.foul ? 'exhausted' : 'celebrate');
    AudioEngine.whoosh();
    this.vibrate(HAPTICS.goodTap);
    if (this.foul) this.cameras.main.flash(200, 255, 60, 60);
  }

  update(_t: number, delta: number): void {
    const dt = delta / 1000;
    this.cameras.main.scrollX = Phaser.Math.Linear(this.cameras.main.scrollX, this.camTarget, 0.12);
    if (this.phase === 'intro' || this.phase === 'done' || this.phase === 'showcase' || this.phase === 'between') { this.rig.update(dt); return; }
    if (this.phase === 'runup') { this.updateRunup(dt); return; }
    if (this.phase === 'flight') { this.updateFlight(dt); }
  }

  private updateRunup(dt: number): void {
    this.acc += dt;
    let steps = 0;
    while (this.acc >= SIM_STEP && steps < MAX_SIM_STEPS_PER_FRAME) {
      // Run zone: drag-based mash physics. Throw zone: hold the acquired speed CONSTANT
      // (no slow-down), so the athlete keeps charging to the line and there is no dead time.
      if (this.aiming) this.velocity = this.acquiredSpeed;
      else this.velocity *= SPRINT.dragPerStep;
      this.runDist += this.velocity * SIM_STEP;
      this.acc -= SIM_STEP;
      steps++;
    }
    const worldX = START_X + this.runDist * PPM;
    // Cross into the throw zone: lock the acquired speed, stop the mash.
    if (!this.aiming && worldX >= this.runEndX) {
      this.aiming = true;
      this.acquiredSpeed = this.velocity;
      this.tapZones.setVisible(false);
    }
    if (worldX >= this.lineX) { this.release(); return; } // reached the line: auto-throw
    this.rig.container.x = worldX;
    this.rig.container.y = this.groundY;
    const sn = Phaser.Math.Clamp(this.velocity / this.maxSpeed, 0, 1);
    this.rig.setMotion(sn);
    this.rig.update(dt);
    // Angle meter sweeps continuously; the release locks it.
    this.angleSweep = 0.5 + 0.5 * Math.sin(this.time.now / 1000 * 3.0);
    this.speedBar.set(this.aiming ? Phaser.Math.Clamp(this.acquiredSpeed / this.maxSpeed, 0, 1) : sn);
    this.angleBar.set(this.angleSweep, this.angleSweep);
    this.camTarget = Phaser.Math.Clamp(worldX - this.W * 0.3, 0, this.cameras.main.getBounds().width - this.W);
    if (this.aiming) {
      const toLine = (this.lineX - worldX) / PPM;
      this.hud.setText(t('game.javAim', { m: Math.max(0, toLine).toFixed(1) }));
    } else {
      this.hud.setText(t('game.runMash'));
    }
    if (this.time.now / 1000 - this.lastTapAt > SPRINT.cadenceWindow) this.cadence = Phaser.Math.Linear(this.cadence, 0, 0.08);
  }

  /** Draw a proper javelin: tapered shaft, metallic tip, coloured fletching, grip. */
  private drawSpear(x: number, y: number, ang: number, len: number, trail: boolean): void {
    const g = this.javelin;
    g.clear();
    const cx = Math.cos(ang), sy = Math.sin(ang), nx = -sy, ny = cx;
    const tipX = x + cx * len * 0.55, tipY = y + sy * len * 0.55;
    const tailX = x - cx * len * 0.45, tailY = y - sy * len * 0.45;
    if (trail) {
      g.lineStyle(6, 0xffffff, 0.1).beginPath();
      g.moveTo(tailX - cx * 55, tailY - sy * 55); g.lineTo(tailX, tailY); g.strokePath();
    }
    g.lineStyle(5, 0xcbb78a, 1).beginPath();
    g.moveTo(tailX, tailY); g.lineTo(tipX, tipY); g.strokePath();
    g.fillStyle(0xd2dcec, 1).fillTriangle(tipX + cx * 13, tipY + sy * 13, tipX + nx * 4.5, tipY + ny * 4.5, tipX - nx * 4.5, tipY - ny * 4.5);
    g.fillStyle(0xff7a59, 1).fillTriangle(tailX - cx * 2, tailY - sy * 2, tailX - cx * 11 + nx * 6, tailY - sy * 11 + ny * 6, tailX - cx * 11 - nx * 6, tailY - sy * 11 - ny * 6);
    g.fillStyle(0x2a3358, 1).fillCircle(x, y, 3.5);
  }

  private updateFlight(dt: number): void {
    this.flightT += dt;
    const p = Phaser.Math.Clamp(this.flightT / this.flightDur, 0, 1);
    const landX = this.lineX + this.throwDist * PPM;
    const x = Phaser.Math.Linear(this.releaseX, landX, p);
    const peak = this.H * 0.34 * Math.sin(this.releaseAngleRad);
    const y = this.groundY - 60 - Math.sin(p * Math.PI) * peak;
    // Spear angle follows the trajectory tangent.
    const tang = Math.cos(p * Math.PI);
    const ang = -this.releaseAngleRad * tang;
    this.drawSpear(x, y, ang, 78, true);
    this.rig.update(dt);
    this.camTarget = Phaser.Math.Clamp(x - this.W * 0.45, 0, this.cameras.main.getBounds().width - this.W);
    if (p >= 1) this.land();
  }

  private land(): void {
    this.phase = 'between';
    const dist = this.foul ? 0 : this.throwDist;
    const landX = this.lineX + this.throwDist * PPM;
    this.cameras.main.shake(120, 0.004);
    AudioEngine.thud();
    this.vibrate(HAPTICS.finish);
    if (!this.foul) {
      this.drawSpear(landX - 14, this.groundY - 30, -Math.max(0.6, this.releaseAngleRad), 80, false);
      const dust = this.add.particles(landX, this.groundY, 'spark', {
        lifespan: 520, speed: { min: 30, max: 130 }, angle: { min: 200, max: 340 },
        scale: { start: 0.5, end: 0 }, alpha: { start: 0.6, end: 0 }, tint: 0xcaa463, gravityY: 320, emitting: false,
      });
      dust.explode(14);
      this.time.delayedCall(800, () => dust.destroy());
    } else {
      this.javelin.clear();
    }

    const isBest = dist > this.playerBest;
    this.playerBest = Math.max(this.playerBest, dist);
    if (isBest && dist > 0) {
      this.playerMarker?.forEach((o) => o.destroy());
      this.playerMarker = this.flagMarker(landX, PALETTE.laneAccent, `TU · ${dist.toFixed(2)}m`, true);
    }
    this.bigText.setText(this.foul ? t('game.foul') : `${dist.toFixed(2)} m${isBest && dist > 0 ? '  ✦' : ''}`)
      .setColor(this.foul ? '#ff5d6c' : '#ffd24a').setAlpha(1).setScale(0.6);
    this.tweens.add({ targets: this.bigText, scale: 1, duration: 400, ease: 'Back.out' });
    AudioEngine.fanfare();

    if (this.attempt < ATTEMPTS) {
      this.attempt++;
      this.time.delayedCall(1600, () => this.beginAttempt());
    } else {
      this.time.delayedCall(1700, () => this.finalize());
    }
  }

  private finalize(): void {
    this.phase = 'done';
    this.controls.setEnabled(false);
    this.throwBtn.setVisible(false);
    this.tapZones.setVisible(false);
    const standings: Standing[] = [
      { name: this.athlete.name, species: this.athlete.species, isPlayer: true, nationality: this.athlete.nationality, metric: this.playerBest },
      ...this.aiThrows.map((a) => ({ name: a.name, species: a.species, isPlayer: false, nationality: a.nationality, metric: a.metric })),
    ].sort((x, y) => y.metric - x.metric);
    const rank = standings.findIndex((s) => s.isPlayer) + 1;
    const score = Math.round(Phaser.Math.Clamp(this.playerBest * 18, 0, 1000));
    const reward = getDiscipline('javelin').scoreToReward(score);
    this.bigText.setText(t('game.bestThrow', { m: this.playerBest.toFixed(2) })).setColor('#ffd24a').setAlpha(1).setScale(0.6);
    this.tweens.add({ targets: this.bigText, scale: 1, duration: 500, ease: 'Back.out' });
    const result: DisciplineResult = {
      disciplineId: 'javelin', metricKind: 'distance', athleteId: this.athlete.id, athleteName: this.athlete.name,
      species: this.athlete.species, nationality: this.athlete.nationality, metric: this.playerBest, score, reward, rank, standings,
    };
    this.time.delayedCall(1600, () => this.finishDiscipline(result));
  }

  private vibrate(pattern: number | number[]): void {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate(pattern); } catch { /* unsupported */ }
    }
  }
}
